
import { useEffect, useState , useRef} from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import swal from "sweetalert";
import Loader from "../Common/Loader";

import {
  getFrequencyAction,
  deleteFrequencyAction,
} from "../../../store/actions/FrequencyAction";

const Frequency = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const isFirstLoad = useRef(true);

  let searchTimeout = null;

  const frequencyList = useSelector((state) => state.frequency.list);
  const numRows = useSelector((state) => state.frequency.num_rows || 0);

  const [searchQueries, setSearchQueries] = useState({
    frequency_text: "",
    frequency_status: "",
  });

  const [sortField, setSortField] = useState("frequency_text");
  const [sortOrder, setSortOrder] = useState("asc");
  const [currentPage, setCurrentPage] = useState(0);
  const rowsPerPage = 10;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Track active search for server-side filtering
  const [activeSearchField, setActiveSearchField] = useState("");
  const [activeSearchKeyword, setActiveSearchKeyword] = useState("");

  useEffect(() => {
    // Check if returning from edit page with state
    if (location?.state?.returnTo) {
      const { currentPage: prevPage, searchQueries: prevSearches, sortField: prevSort, sortOrder: prevOrder } = location.state.returnTo;
      setCurrentPage(prevPage || 0);
      setSearchQueries(prevSearches || {});
      setSortField(prevSort || "frequency_text");
      setSortOrder(prevOrder || "asc");

      // Determine the search field and keyword from restored state
      const searchField = prevSearches?.frequency_text ? "frequency_text" : prevSearches?.frequency_status ? "frequency_status" : "";
      const searchKeyword = prevSearches?.frequency_text || prevSearches?.frequency_status || "";

      setActiveSearchField(searchField);
      setActiveSearchKeyword(searchKeyword);

      // Fetch with restored state
      fetchFrequencyFromServer(
        searchField,
        searchKeyword,
        true,
        prevPage || 0,
        prevSort || "frequency_text",
        prevOrder || "asc"
      );
    } else {
      fetchFrequencyFromServer();
    }
  }, []);

  const fetchFrequencyFromServer = async (
    search_by = "",
    search_keyword = "",
    pagination = true,
    pageid = 0,
    sort_by = sortField,
    sort_order = sortOrder
  ) => {
    try {
      // ✅ Show loader only on first visit
      if (isFirstLoad.current) {
        setLoading(true);
      }
  
      await dispatch(
        getFrequencyAction(
          pagination,
          pageid,
          search_by,
          search_keyword,
          sort_by,
          sort_order
        )
      );
    } catch (err) {
      console.error("Failed to fetch frequency:", err);
      setError("Failed to load frequency data.");
    } finally {
      setLoading(false);
      isFirstLoad.current = false; // ✅ stop loader forever
    }
  };
  

  // Handle search - clear other fields
  const handleSearch = (e, field) => {
    const keyword = e.target.value;
  
    const clearedQueries = {
      frequency_text: "",
      frequency_status: "",
    };
  
    const updatedQueries = { ...clearedQueries, [field]: keyword };
  
    setSearchQueries(updatedQueries);
    setCurrentPage(0);
    setActiveSearchField(field);
    setActiveSearchKeyword(keyword);
  
    // ✅ debounce API call
    if (searchTimeout) clearTimeout(searchTimeout);
  
    searchTimeout = setTimeout(() => {
      fetchFrequencyFromServer(
        field,
        keyword,
        true,
        0,
        sortField,
        sortOrder
      );
    }, 400);
  };
  

  const handleSort = (field) => {
    const order = sortField === field && sortOrder === "asc" ? "desc" : "asc";
    setSortField(field);
    setSortOrder(order);
    setCurrentPage(0);
    fetchFrequencyFromServer(activeSearchField, activeSearchKeyword, true, 0, field, order);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchFrequencyFromServer(activeSearchField, activeSearchKeyword, true, page, sortField, sortOrder);
  };

  const handleDelete = (id) => {
    swal({
      title: "Are you sure?",
      text: "This record will be deleted permanently!",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (yes) => {
      if (yes) {
        try {
          setLoading(true);
          await dispatch(deleteFrequencyAction(id));
          // Refresh the current page after deletion
          await fetchFrequencyFromServer(
            activeSearchField,
            activeSearchKeyword,
            true,
            currentPage,
            sortField,
            sortOrder
          );
        } catch (err) {
          setError("Failed to delete frequency");
          console.error("Delete failed:", err);
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const totalPages = Math.ceil(numRows / rowsPerPage);

  return ( <>
    <div className="row mb-3">
      <div className="col-12 text-end">
        <Link
          to="/add-frequency"
          state={{
            returnTo: {
              currentPage,
              searchQueries,
              sortField,
              sortOrder,
            },
          }}
          className="btn btn-primary"
        >
          + Add Frequency
        </Link>
      </div>
    </div>

    <div className="card">
      <div className="card-body">
        {error && <div className="text-danger mb-2">{error}</div>}
        {loading ? (
          <div className="text-center my-5">
            <Loader size="lg" />
          </div>
        ) : (
          <>
          <div className="table-responsive">
            <table className="table table-striped">
              <thead className="table-light">
                <tr>
                  {/* <th>#</th> */}
                  <th onClick={() => handleSort("frequency_text")} style={{ cursor: "pointer" }}>
                    Frequency {sortField === "frequency_text" && (sortOrder === "asc" ? "↑" : "↓")}
                    <input
                      className="form-control form-control-sm mt-1"
                      placeholder="Search frequency"
                      value={searchQueries.frequency_text}
                      onChange={(e) => handleSearch(e, "frequency_text")}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </th>
                  <th onClick={() => handleSort("frequency_status")} style={{ cursor: "pointer" }}>
                    Status {sortField === "frequency_status" && (sortOrder === "asc" ? "↑" : "↓")}
                    <input
                      className="form-control form-control-sm mt-1"
                      placeholder="Search status"
                      value={searchQueries.frequency_status}
                      onChange={(e) => handleSearch(e, "frequency_status")}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>

              <tbody>
                {frequencyList?.length > 0 ? (
                  frequencyList.map((item, index) => (
                    <tr key={item.frequency_id}>
                      {/* <td>{currentPage * rowsPerPage + index + 1}</td> */}
                      <td>{item.frequency_text}</td>
                      <td>
                        <span
                          className={`badge px-3 py-1 rounded-pill text-white ${
                            item.frequency_status === "Active"
                              ? "bg-success"
                              : "bg-danger"
                          }`}
                        >
                          {item.frequency_status}
                        </span>
                      </td>
                      <td>
                        <div className="d-flex">
                          <button
                            className="btn btn-link p-0 me-3"
                            onClick={() =>
                              navigate("/add-frequency", {
                                state: {
                                  item,
                                  returnTo: {
                                    currentPage,
                                    searchQueries,
                                    sortField,
                                    sortOrder,
                                  },
                                },
                              })
                            }
                          >
                            <i className="fa fa-pencil-alt text-success"></i>
                          </button>

                          <button
                            className="btn btn-link p-0"
                            onClick={() =>
                              handleDelete(item.frequency_id)
                            }
                          >
                            <i className="fa fa-trash text-danger"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="text-center">
                      No Frequency Found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
  {/* Pagination */}
  <div
          className="pagination-wrapper"
          style={{
            display: "flex",
            gap: "6px",
            alignItems: "center",
            justifyContent: "center",
            marginTop: "20px",
            flexWrap: "wrap",
            flexDirection: "column",
          }}
        >
          <div>
            Showing {frequencyList?.length === 0 ? 0 : currentPage * rowsPerPage + 1} to{" "}
            {Math.min((currentPage + 1) * rowsPerPage, numRows)} of {numRows} entries
          </div>
          <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", justifyContent: "center" }}>
            <button
              onClick={() => handlePageChange(Math.max(currentPage - 1, 0))}
              disabled={currentPage === 0}
              style={{
                padding: "6px 12px",
                border: "1px solid #ddd",
                borderRadius: "4px",
                backgroundColor: "#fff",
                color: "#000",
                cursor: currentPage === 0 ? "not-allowed" : "pointer",
                opacity: currentPage === 0 ? 0.5 : 1,
              }}
            >
              {"<"}
            </button>
            {(() => {
              const pages = [];
              const maxPagesToShow = 5;
              const startPage = Math.max(
                0,
                Math.min(currentPage - 2, totalPages - maxPagesToShow)
              );
              const endPage = Math.min(startPage + maxPagesToShow, totalPages);

              if (startPage > 0) {
                pages.push(
                  <button
                    key={0}
                    onClick={() => handlePageChange(0)}
                    style={{
                      padding: "6px 12px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      backgroundColor: "#fff",
                      color: "#000",
                      cursor: "pointer",
                    }}
                  >
                    1
                  </button>
                );
                if (startPage > 1) {
                  pages.push(
                    <span
                      key="dots1"
                      style={{ padding: "6px 12px", userSelect: "none" }}
                    >
                      ...
                    </span>
                  );
                }
              }

              for (let i = startPage; i < endPage; i++) {
                const isActive = currentPage === i;
                pages.push(
                  <button
                    key={i}
                    onClick={() => handlePageChange(i)}
                    style={{
                      padding: "6px 12px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      backgroundColor: isActive ? "#007bff" : "#fff",
                      color: isActive ? "#fff" : "#000",
                      cursor: "pointer",
                    }}
                  >
                    {i + 1}
                  </button>
                );
              }

              if (endPage < totalPages) {
                if (endPage < totalPages - 1) {
                  pages.push(
                    <span
                      key="dots2"
                      style={{ padding: "6px 12px", userSelect: "none" }}
                    >
                      ...
                    </span>
                  );
                }
                pages.push(
                  <button
                    key={totalPages - 1}
                    onClick={() => handlePageChange(totalPages - 1)}
                    style={{
                      padding: "6px 12px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      backgroundColor: "#fff",
                      color: "#000",
                      cursor: "pointer",
                    }}
                  >
                    {totalPages}
                  </button>
                );
              }

              return pages;
            })()}
            <button
              onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages - 1))}
              disabled={currentPage === totalPages - 1}
              style={{
                padding: "6px 12px",
                border: "1px solid #ddd",
                borderRadius: "4px",
                backgroundColor: "#fff",
                color: "#000",
                cursor: currentPage === totalPages - 1 ? "not-allowed" : "pointer",
                opacity: currentPage === totalPages - 1 ? 0.5 : 1,
              }}
            >
              {">"}
            </button>
          </div>
        </div>
          </>
        )}

      
      </div>
    </div>
    </>
  );
};

export default Frequency;
